import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { Select2 } from '@/components/ui/Select2'
import { useAuth } from '@/contexts/AuthContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { useMarket } from '@/contexts/MarketContext'
import { ConnectionBadge } from './ConnectionBadge'
import { AccountMetrics } from './AccountMetrics'
import { cn } from '@/lib/cn'

/**
 * Institutional top navigation — logo, ticker switcher, marquee,
 * WS status, i18n/theme, notifications, profile/logout.
 */
export function TerminalHeader({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { logout, isAuthenticated } = useAuth()
  const { items, unreadCount, markAllRead, markRead } = useNotifications()
  const { instruments, active, setSymbol } = useMarket()
  const [notifOpen, setNotifOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)
  const profileRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (notifRef.current && !notifRef.current.contains(target)) {
        setNotifOpen(false)
      }
      if (profileRef.current && !profileRef.current.contains(target)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const tickerOptions = instruments.map((i) => ({
    value: i.symbol,
    label: i.label,
    description: i.tvSymbol,
  }))

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-20 flex h-12 shrink-0 items-center gap-2 border-b border-terminal-border/40',
          'bg-terminal-bg/85 px-3 backdrop-blur-md',
          className,
        )}
      >
        {/* Left: brand + ticker switcher */}
        <div className="flex shrink-0 items-center gap-2">
          <BrandLogo height={22} className="hidden sm:block" />
          <div className="w-[9.5rem] sm:w-[11rem]">
            <Select2
              options={tickerOptions}
              value={active.symbol}
              onChange={setSymbol}
              searchable
              aria-label={t('header.tickerSearch')}
              className="[&_button]:h-7 [&_button]:rounded-sm [&_button]:text-[11px] [&_button]:font-mono"
            />
          </div>
        </div>

        {/* Center: account metrics */}
        <AccountMetrics />

        {/* Right: controls */}
        <div className="ms-auto flex shrink-0 items-center gap-1.5">
          <ConnectionBadge />
          <LanguageSwitcher />
          <ThemeSwitcher />

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              onClick={() => {
                setNotifOpen((o) => !o)
                setProfileOpen(false)
                if (!notifOpen) markAllRead()
              }}
              className={cn(
                'relative inline-flex h-7 w-7 items-center justify-center rounded-sm',
                'border border-terminal-border/40 text-terminal-muted',
                'transition-colors hover:bg-terminal-elevated hover:text-terminal-fg',
              )}
              aria-label={t('notifications.title')}
            >
              <BellIcon />
              {unreadCount > 0 ? (
                <span className="absolute -end-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-danger px-0.5 text-[9px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              ) : null}
            </button>

            {notifOpen ? (
              <div
                className={cn(
                  'absolute end-0 top-[calc(100%+6px)] z-floating w-80 overflow-hidden rounded-md border',
                  'border-terminal-border/40 bg-terminal-surface shadow-2xl',
                )}
              >
                <div className="flex items-center justify-between border-b border-terminal-border/40 px-3 py-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
                    {t('notifications.title')}
                  </p>
                  <button
                    type="button"
                    className="text-[11px] text-accent"
                    onClick={() => setNotifOpen(false)}
                  >
                    {t('actions.close')}
                  </button>
                </div>
                <ul className="terminal-scroll max-h-72 overflow-y-auto">
                  {items.length === 0 ? (
                    <li className="px-3 py-6 text-center text-xs text-terminal-muted">
                      {t('notifications.empty')}
                    </li>
                  ) : (
                    items.map((n) => (
                      <li key={n.uuid}>
                        <button
                          type="button"
                          onClick={() => markRead(n.uuid)}
                          className={cn(
                            'flex w-full flex-col gap-0.5 border-b border-terminal-border/40 px-3 py-1.5 text-start',
                            'hover:bg-terminal-elevated/50',
                            !n.read && 'bg-bid-badge',
                          )}
                        >
                          <span className="text-xs font-medium text-terminal-fg">
                            {t(n.title, { defaultValue: n.title })}
                          </span>
                          <span className="text-[11px] text-terminal-muted">
                            {t(n.content, { defaultValue: n.content })}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            ) : null}
          </div>

          {/* Profile / logout */}
          {isAuthenticated ? (
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => {
                  setProfileOpen((o) => !o)
                  setNotifOpen(false)
                }}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-sm border border-terminal-border/40',
                  'bg-terminal-surface px-2 text-[11px] font-medium text-terminal-muted',
                  'hover:bg-terminal-elevated hover:text-terminal-fg',
                )}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
              >
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-accent/20 text-[9px] font-bold text-accent">
                  T
                </span>
                <span className="hidden sm:inline">{t('nav.account')}</span>
                <ChevronIcon open={profileOpen} />
              </button>

              {profileOpen ? (
                <div
                  role="menu"
                  className={cn(
                    'absolute end-0 top-[calc(100%+6px)] z-floating w-44 overflow-hidden rounded-md border',
                    'border-terminal-border/40 bg-terminal-surface py-1 shadow-2xl',
                  )}
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setProfileOpen(false)
                      setLogoutOpen(true)
                    }}
                    className="flex w-full px-3 py-1.5 text-start text-xs text-danger hover:bg-danger/10"
                  >
                    {t('actions.logout')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      <ConfirmModal
        open={logoutOpen}
        title={t('logout_confirm_title')}
        message={t('logout_confirm_message')}
        tone="danger"
        confirmLabel={t('actions.logout')}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={() => {
          void logout().finally(() => {
            setLogoutOpen(false)
            window.location.assign('/login')
          })
        }}
      />
    </>
  )
}

function BellIcon() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <path
        d="M10 2.5a4.5 4.5 0 0 0-4.5 4.5v2.2c0 .5-.2 1-.5 1.4L3.8 12.4A1 1 0 0 0 4.6 14h10.8a1 1 0 0 0 .8-1.6l-1.2-1.8a2.2 2.2 0 0 1-.5-1.4V7A4.5 4.5 0 0 0 10 2.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M8 14.5a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      className={cn(
        'h-2.5 w-2.5 transition-transform',
        open && 'rotate-180',
      )}
      fill="none"
      aria-hidden
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}
